import { z } from 'zod';
import { certaintyValues, packIds } from './items';
import { providerRoutes } from './packs/cleaning';

export const EvidencePhotoSchema = z.object({
  id: z.string().uuid(),
  itemId: z.string().min(1),
  name: z.string().min(1).max(120),
  mimeType: z.enum(['image/jpeg','image/png','image/webp']),
  byteSize: z.number().int().min(1).max(450_000),
  dataUrl: z.string().startsWith('data:image/').max(650_000),
  createdAt: z.string()
});
export type EvidencePhoto = z.infer<typeof EvidencePhotoSchema>;

export const aftercareCategories = ['damage','missing_item','additional_charge','unfinished_work','other'] as const;
export const aftercareStatuses = ['recorded','request_sent','reply_received','closed'] as const;
export const aftercareEventKinds = ['recorded','request_sent','reply_received','closed','reopened'] as const;

export const AftercareEventSchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(aftercareEventKinds),
  text: z.string().max(500),
  occurredAt: z.string()
});
export const AftercareRecordSchema = z.object({
  id: z.string().uuid(),
  category: z.enum(aftercareCategories),
  subject: z.string().min(1).max(80),
  observedFacts: z.string().min(1).max(1000),
  requestedAction: z.string().min(1).max(500),
  occurredAt: z.string(),
  status: z.enum(aftercareStatuses),
  relatedItemIds: z.array(z.string().min(1)).max(8),
  confirmedAmountKrw: z.number().int().min(0).max(100_000_000).nullable(),
  evidencePhotos: z.array(EvidencePhotoSchema).max(3),
  events: z.array(AftercareEventSchema).min(1).max(40),
  createdAt: z.string(),
  updatedAt: z.string()
});
export type AftercareEvent = z.infer<typeof AftercareEventSchema>;
export type AftercareRecord = z.infer<typeof AftercareRecordSchema>;

export const cleaningCycleStatuses = ['planned','completed'] as const;
export const CleaningCycleSchema = z.object({
  id: z.string().uuid(),
  serviceDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  status: z.enum(cleaningCycleStatuses),
  scopeItemIds: z.array(z.string().min(1)).min(1).max(22),
  note: z.string().max(500),
  confirmedAmountKrw: z.number().int().min(0).max(100_000_000).nullable(),
  createdAt: z.string(),
  updatedAt: z.string()
});
export type CleaningCycle = z.infer<typeof CleaningCycleSchema>;

export const agreementChangeKinds = ['included','excluded','amount','condition','reverted'] as const;
export const agreementStates = ['confirmed','conditional','unanswered'] as const;
export const StoredItemSchema = z.object({itemId:z.string(),certainty:z.enum(certaintyValues),amountKrw:z.number().int().min(0).max(100000000).nullable(),sourceText:z.string().max(160).nullable(),confidence:z.number().min(0).max(1),userEdited:z.boolean()});
export const AgreementChangeSchema = z.object({
  id: z.string().uuid(),
  itemId: z.string().min(1),
  kind: z.enum(agreementChangeKinds),
  state: z.enum(agreementStates),
  beforeSummary: z.string().max(500),
  afterSummary: z.string().max(500),
  sourceExcerpt: z.string().min(1).max(500),
  confirmedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  revertsChangeId: z.string().uuid().nullable().default(null),
  createdAt: z.string(),
  /** 새 회신은 최초 견적을 덮어쓰지 않는다. 취소하면 원래 항목이 다시 보인다. */
  estimateId: z.string().uuid().optional(),
  itemSnapshot: z.object({before:StoredItemSchema,after:StoredItemSchema}).optional()
});
export type AgreementChange = z.infer<typeof AgreementChangeSchema>;

export const followUpStatuses = ['open','replied','resolved','reopened'] as const;
export const FollowUpEventSchema = z.object({
  id: z.string().uuid(), status: z.enum(followUpStatuses), note: z.string().max(500), occurredAt: z.string()
});
export const FollowUpSchema = z.object({
  id: z.string().uuid(),
  subject: z.string().min(1).max(100),
  observedFacts: z.string().min(1).max(1000),
  relatedItemId: z.string().min(1).nullable(),
  dueOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  status: z.enum(followUpStatuses),
  events: z.array(FollowUpEventSchema).min(1).max(50),
  createdAt: z.string(),
  updatedAt: z.string()
});
export type FollowUp = z.infer<typeof FollowUpSchema>;

export const contractNoticeKinds = ['cancellation','postponement','no_show'] as const;
export const contractNoticeChannels = ['kakao','sms','phone','email','other'] as const;
export const ContractNoticeSchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(contractNoticeKinds),
  channel: z.enum(contractNoticeChannels),
  receivedAt: z.string(),
  effectiveOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  contactAttempts:z.array(z.string()).max(30).optional(),
  originalMessage: z.string().min(1).max(1000),
  createdAt: z.string()
});
export type ContractNotice = z.infer<typeof ContractNoticeSchema>;

export const settlementKinds = ['deposit','balance','extra_charge','refund'] as const;
export const settlementMethods = ['bank_transfer','card','cash','other'] as const;
export const receiptStatuses = ['not_requested','requested','received','not_applicable'] as const;
export const SettlementRecordSchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(settlementKinds),
  amountKrw: z.number().int().min(0).max(100_000_000).nullable(),
  itemId:z.string().nullable().optional(),
  demandedOnSite:z.boolean().optional(),
  occurredOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  method: z.enum(settlementMethods),
  receiptStatus: z.enum(receiptStatuses),
  note: z.string().max(500),
  createdAt: z.string(),
  updatedAt: z.string()
});
export type SettlementRecord = z.infer<typeof SettlementRecordSchema>;

export const ConfirmationRequestSchema = z.object({
  id:z.string().uuid(), estimateId:z.string().uuid(), itemIds:z.array(z.string()).min(1).max(22),
  text:z.string().min(1).max(10000), createdAt:z.string(),
  events:z.array(z.object({kind:z.enum(['copied','sent','send_reverted']),occurredAt:z.string()})).min(1).max(50)
});
export type ConfirmationRequest = z.infer<typeof ConfirmationRequestSchema>;

export const ProjectSchema = z.object({
  schemaVersion: z.literal(1),
  id: z.string().uuid(),
  title: z.string().min(1).max(80),
  /** 팩 승격(2026-08-14) 전에 저장된 프로젝트는 이 값이 없다. 이사 팩으로 읽는다. */
  packId: z.enum(packIds).default('moving'),
  /** 청소 팩의 계약 상대 경로. providerBranch가 false인 팩에서는 항상 null. */
  providerRoute: z.enum(providerRoutes).nullable().default(null),
  /** 서비스일. 이사는 이사일, 청소는 방문일. 저장 호환을 위해 필드명을 유지한다. */
  moveDate: z.string().nullable(),
  estimates: z.array(z.object({
    id: z.string().uuid(), name: z.string().min(1).max(80), createdAt: z.string(),
    revisionOf: z.string().uuid().nullable().optional(),
    companyName: z.string().max(80).optional(),
    quotedAt: z.string().nullable().optional(),
    sourceKind: z.enum(['structured_manual','pasted_text','document_analysis']).optional(),
    evidencePhotos: z.array(EvidencePhotoSchema).max(22).optional(),
    items: z.array(StoredItemSchema)
  })).max(9),
  responses: z.array(z.object({ id:z.string().uuid(), itemId:z.string(), text:z.string().max(500), createdAt:z.string(), updatedAt:z.string().optional() })),
  /** 복사와 발송은 다르며, 이전 보관본에는 이 필드가 없다. */
  confirmationRequests:z.array(ConfirmationRequestSchema).max(100).optional(),
  inventory:z.array(z.object({id:z.string().uuid(),label:z.string().min(1).max(80),count:z.number().int().min(1).max(999),note:z.string().max(160)})).max(60).optional(),
  preflight:z.object({checkedAt:z.string(),answers:z.array(z.boolean()).length(5)}).nullable().optional(),
  finalContract:z.object({text:z.string().max(10000),comparedAt:z.string(),previousComparedAt:z.array(z.string()).optional()}).nullable().optional(),
  closedAt:z.string().nullable().optional(),
  cycleRepeat:z.enum(['weekly','fortnightly','monthly']).nullable().optional(),
  checks: z.record(z.string(), z.boolean()),
  /** 이사 뒤 관찰 사실·요청·회신을 묶는다. 책임·배상 판단은 저장하지 않는다. */
  aftercare: z.array(AftercareRecordSchema).max(20).default([]),
  /** 청소 방문별 합의 범위를 보존해 다음 회차와 직접 비교한다. */
  serviceCycles: z.array(CleaningCycleSchema).max(24).default([]),
  /** 견적 뒤 사용자가 확인한 항목별 변경. 원문 회신은 자동 확정하지 않는다. */
  agreementChanges: z.array(AgreementChangeSchema).max(100).default([]),
  /** 서비스 종료 전후 미완료 사실과 업체가 말한 다음 확인일. */
  followUps: z.array(FollowUpSchema).max(50).default([]),
  /** 취소·연기 사실의 수신 원문. 위약금·책임 판단은 저장하지 않는다. */
  contractNotices: z.array(ContractNoticeSchema).max(30).default([]),
  /** 실제 지급·환불과 증빙 수령 상태. 계좌·카드번호는 저장하지 않는다. */
  settlements: z.array(SettlementRecordSchema).max(100).default([]),
  updatedAt: z.string()
});
export type Project = z.infer<typeof ProjectSchema>;

export const ProjectBackupSchema = z.object({
  format: z.literal('hwakdap-backup'),
  schemaVersion: z.literal(1),
  exportedAt: z.string(),
  activeProjectId: z.string().uuid().nullable(),
  projects: z.array(ProjectSchema).max(50)
}).superRefine((backup, context) => {
  if (backup.activeProjectId && !backup.projects.some((project) => project.id === backup.activeProjectId)) {
    context.addIssue({ code:'custom', path:['activeProjectId'], message:'활성 프로젝트가 보관본에 없어요.' });
  }
  if (new Set(backup.projects.map((project) => project.id)).size !== backup.projects.length) {
    context.addIssue({ code:'custom', path:['projects'], message:'프로젝트 ID가 중복돼요.' });
  }
});
export type ProjectBackup = z.infer<typeof ProjectBackupSchema>;

const DB_NAME = 'hwakdap';
const DB_VERSION = 1;
const PROJECT_STORE = 'projects';
const META_STORE = 'meta';
const ACTIVE_KEY = 'activeProjectId';
const LEGACY_KEY = 'hwakdap.project.v1';
const MAX_PROJECTS = 50;

type MetaRecord = { key:string; value:string|null };

function requestResult<T>(request:IDBRequest<T>) {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('브라우저 저장소 요청에 실패했어요.'));
  });
}

function transactionDone(transaction:IDBTransaction) {
  return new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error('브라우저 저장소 변경을 완료하지 못했어요.'));
    transaction.onerror = () => reject(transaction.error ?? new Error('브라우저 저장소 변경에 실패했어요.'));
  });
}

function openDatabase() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(PROJECT_STORE)) {
        const store = database.createObjectStore(PROJECT_STORE, { keyPath:'id' });
        store.createIndex('updatedAt', 'updatedAt');
      }
      if (!database.objectStoreNames.contains(META_STORE)) database.createObjectStore(META_STORE, { keyPath:'key' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('브라우저 저장소를 열지 못했어요.'));
  });
}

async function migrateLegacyProject(database:IDBDatabase) {
  if (typeof localStorage === 'undefined') return;
  const raw = localStorage.getItem(LEGACY_KEY);
  if (!raw) return;
  let legacy:Project;
  try { legacy = ProjectSchema.parse(JSON.parse(raw)); } catch { return; }
  const transaction = database.transaction([PROJECT_STORE, META_STORE], 'readwrite');
  const projects = transaction.objectStore(PROJECT_STORE);
  const existingCount = await requestResult(projects.count());
  if (existingCount === 0) {
    projects.put(legacy);
    transaction.objectStore(META_STORE).put({ key:ACTIVE_KEY, value:legacy.id } satisfies MetaRecord);
  }
  await transactionDone(transaction);
  localStorage.removeItem(LEGACY_KEY);
}

async function databaseReady() {
  const database = await openDatabase();
  await migrateLegacyProject(database);
  return database;
}

export async function loadProjects() {
  const database = await databaseReady();
  const transaction = database.transaction(PROJECT_STORE, 'readonly');
  const projects = (await requestResult(transaction.objectStore(PROJECT_STORE).getAll()) as unknown[])
    .map((project) => ProjectSchema.parse(project))
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
  await transactionDone(transaction);
  database.close();
  return projects;
}

export async function loadActiveProjectId() {
  const database = await databaseReady();
  const transaction = database.transaction(META_STORE, 'readonly');
  const record = await requestResult(transaction.objectStore(META_STORE).get(ACTIVE_KEY)) as MetaRecord|undefined;
  await transactionDone(transaction);
  database.close();
  return typeof record?.value === 'string' ? record.value : null;
}

export async function loadProject(id?:string|null) {
  const database = await databaseReady();
  const transaction = database.transaction([PROJECT_STORE, META_STORE], 'readonly');
  const meta = id ? null : await requestResult(transaction.objectStore(META_STORE).get(ACTIVE_KEY)) as MetaRecord|undefined;
  const projectId = id ?? meta?.value ?? null;
  const raw = projectId ? await requestResult(transaction.objectStore(PROJECT_STORE).get(projectId)) : null;
  await transactionDone(transaction);
  database.close();
  return raw ? ProjectSchema.parse(raw) : null;
}

export async function saveProject(input:Project) {
  const project = ProjectSchema.parse(input);
  const database = await databaseReady();
  const transaction = database.transaction([PROJECT_STORE, META_STORE], 'readwrite');
  const projects = transaction.objectStore(PROJECT_STORE);
  const [existing, count] = await Promise.all([requestResult(projects.get(project.id)), requestResult(projects.count())]);
  if (!existing && count >= MAX_PROJECTS) {
    transaction.abort();
    database.close();
    throw new Error('이 기기에는 이사 기록을 최대 50개까지 보관할 수 있어요.');
  }
  projects.put(project);
  transaction.objectStore(META_STORE).put({ key:ACTIVE_KEY, value:project.id } satisfies MetaRecord);
  await transactionDone(transaction);
  database.close();
  return project;
}

export async function activateProject(id:string) {
  const project = await loadProject(id);
  if (!project) throw new Error('프로젝트를 찾지 못했어요.');
  const database = await databaseReady();
  const transaction = database.transaction(META_STORE, 'readwrite');
  transaction.objectStore(META_STORE).put({ key:ACTIVE_KEY, value:id } satisfies MetaRecord);
  await transactionDone(transaction);
  database.close();
}

export async function deleteProject(id:string) {
  const database = await databaseReady();
  const transaction = database.transaction([PROJECT_STORE, META_STORE], 'readwrite');
  const projects = transaction.objectStore(PROJECT_STORE);
  projects.delete(id);
  const remaining = (await requestResult(projects.getAll()) as Project[])
    .filter((project) => project.id !== id)
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
  transaction.objectStore(META_STORE).put({ key:ACTIVE_KEY, value:remaining[0]?.id ?? null } satisfies MetaRecord);
  await transactionDone(transaction);
  database.close();
}

export async function deleteAllProjects() {
  const database = await databaseReady();
  const transaction = database.transaction([PROJECT_STORE, META_STORE], 'readwrite');
  transaction.objectStore(PROJECT_STORE).clear();
  transaction.objectStore(META_STORE).put({ key:ACTIVE_KEY, value:null } satisfies MetaRecord);
  await transactionDone(transaction);
  database.close();
}

export function exportProjects(projects:Project[], activeProjectId:string|null) {
  return JSON.stringify(ProjectBackupSchema.parse({
    format:'hwakdap-backup', schemaVersion:1, exportedAt:new Date().toISOString(), activeProjectId, projects
  }), null, 2);
}

export function mergeProjectRecords(existing:Project[],incoming:Project[]){
  const merged=new Map(existing.map(p=>[p.id,p]));
  for(const project of incoming){const previous=merged.get(project.id);if(!previous||project.updatedAt>previous.updatedAt)merged.set(project.id,project);}
  if(merged.size>MAX_PROJECTS)throw new Error('기기에는 계약을 최대 50개까지 보관할 수 있어요.');
  return [...merged.values()];
}
export function exportProject(project:Project){return exportProjects([project],project.id);}
export async function importProjects(raw:string,mode:'merge'|'replace'='merge') {
  const backup = ProjectBackupSchema.parse(JSON.parse(raw));
  const database = await databaseReady();
  const transaction = database.transaction([PROJECT_STORE, META_STORE], 'readwrite');
  const store=transaction.objectStore(PROJECT_STORE);
  const existing=(await requestResult(store.getAll()) as unknown[]).map(p=>ProjectSchema.parse(p));
  let merged:Project[];
  try{merged=mode==='merge'?mergeProjectRecords(existing,backup.projects):backup.projects;}catch(cause){transaction.abort();database.close();throw cause;}
  if(mode==='replace')store.clear();
  for(const project of merged)store.put(project);
  const active=backup.activeProjectId??merged[0]?.id??null;
  transaction.objectStore(META_STORE).put({key:ACTIVE_KEY,value:active} satisfies MetaRecord);
  await transactionDone(transaction);database.close();return {...backup,projects:merged,activeProjectId:active};
}
